import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Number of reels kept mounted ahead of the active card. Update this constant to tweak
 * the size of the visible queue when experimenting with different buffer sizes.
 */
const DEFAULT_QUEUE_SIZE = 3;

/**
 * Number of previously viewed reels kept alive to allow smooth reverse scrolling.
 * Values above 2 will keep more historical clips mounted, while lower values aggressively
 * release older embeds.
 */
const PREVIOUS_BUFFER = 2;

const INTERSECTION_THRESHOLDS = [0.25, 0.5, 0.75];

const HEIGHT_EPSILON = 0.5;

const MEASUREMENT_RETENTION_MULTIPLIER = 3;

/**
 * Derives a windowed subset of reels around the currently focused card so only nearby clips
 * remain mounted. The hook keeps the active reel plus the next two clips in memory while
 * providing spacer measurements to preserve scroll snapping for virtualised content.
 *
 * @template T
 * @param {object} params - Hook parameters.
 * @param {T[]} params.items - Full reel collection ordered for scrolling.
 * @param {import('react').RefObject<HTMLElement>} params.containerRef - Scroll container ref.
 * @param {number} [params.queueSize=DEFAULT_QUEUE_SIZE] - Number of clips ahead of the active reel
 * to keep mounted (active + queueSize - 1).
 * @returns {object} Windowing helpers and metadata.
 */
export function useReelWindow({ items, containerRef, queueSize = DEFAULT_QUEUE_SIZE }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);

  const measuredHeightsRef = useRef(new Map());
  const fallbackHeightRef = useRef(0);
  const elementByIndexRef = useRef(new Map());
  const indexByElementRef = useRef(new WeakMap());
  const observerRef = useRef(null);
  const previousBatchRef = useRef(null);
  const measurementQueueRef = useRef(new Map());
  const measurementFrameRef = useRef(null);
  const mountedRangeRef = useRef({ start: 0, end: 0 });

  const { scheduleFrame, cancelFrame } = useMemo(() => {
    const hasWindow = typeof window !== 'undefined';
    const schedule =
      hasWindow && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => setTimeout(callback, 16);
    const cancel =
      hasWindow && typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame.bind(window)
        : (handle) => clearTimeout(handle);

    return { scheduleFrame: schedule, cancelFrame: cancel };
  }, []);

  const [measurementVersion, setMeasurementVersion] = useState(0);

  const totalCount = items.length;
  const windowCapacity = useMemo(
    () => Math.max(1, PREVIOUS_BUFFER + queueSize),
    [queueSize],
  );

  // Reset the active index when a new batch of reels arrives so we always start from the top.
  useEffect(() => {
    const nextBatchId = items[0]?.batchId ?? null;
    if (items.length === 0) {
      previousBatchRef.current = null;
      activeIndexRef.current = 0;
      setActiveIndex(0);
      measurementQueueRef.current.clear();
      if (measurementFrameRef.current !== null) {
        cancelFrame(measurementFrameRef.current);
        measurementFrameRef.current = null;
      }
      return;
    }

    if (previousBatchRef.current !== nextBatchId) {
      previousBatchRef.current = nextBatchId;
      measuredHeightsRef.current.clear();
      measurementQueueRef.current.clear();
      if (measurementFrameRef.current !== null) {
        cancelFrame(measurementFrameRef.current);
        measurementFrameRef.current = null;
      }
      fallbackHeightRef.current = 0;
      setMeasurementVersion((version) => version + 1);
      activeIndexRef.current = 0;
      setActiveIndex(0);
    }
  }, [cancelFrame, items]);

  // Clamp the active index when the collection shrinks.
  useEffect(() => {
    if (!totalCount) {
      if (activeIndexRef.current !== 0) {
        activeIndexRef.current = 0;
        setActiveIndex(0);
      }
      return;
    }

    if (activeIndexRef.current > totalCount - 1) {
      const nextIndex = totalCount - 1;
      activeIndexRef.current = nextIndex;
      setActiveIndex(nextIndex);
    }
  }, [totalCount]);

  const startIndex = useMemo(
    () => Math.max(0, activeIndex - PREVIOUS_BUFFER),
    [activeIndex],
  );

  const endIndex = useMemo(
    () => Math.min(totalCount, activeIndex + queueSize),
    [activeIndex, queueSize, totalCount],
  );

  const mountedRange = useMemo(
    () => ({
      start: startIndex,
      end: endIndex,
    }),
    [startIndex, endIndex],
  );

  useEffect(() => {
    mountedRangeRef.current = { start: mountedRange.start, end: mountedRange.end };
  }, [mountedRange.end, mountedRange.start]);

  const measurementRetention = useMemo(
    () => Math.max(windowCapacity, 1) * MEASUREMENT_RETENTION_MULTIPLIER,
    [windowCapacity],
  );

  const queue = useMemo(() => {
    if (!totalCount) {
      return [];
    }
    const queueEnd = Math.min(totalCount, activeIndex + queueSize);
    return items.slice(activeIndex, queueEnd).map((item, offset) => ({
      item,
      index: activeIndex + offset,
    }));
  }, [activeIndex, items, queueSize, totalCount]);

  const computeSpacerHeight = useCallback(
    (from, to) => {
      if (from >= to) {
        return 0;
      }
      const fallbackHeight =
        fallbackHeightRef.current || containerRef.current?.clientHeight || 0;
      let total = 0;
      for (let index = from; index < to; index += 1) {
        const measured = measuredHeightsRef.current.get(index);
        total += measured ?? fallbackHeight;
      }
      return total;
    },
    [containerRef],
  );

  const beforeSpacerHeight = useMemo(
    () => computeSpacerHeight(0, mountedRange.start),
    [computeSpacerHeight, mountedRange.start, measurementVersion],
  );

  const afterSpacerHeight = useMemo(
    () => computeSpacerHeight(mountedRange.end, totalCount),
    [computeSpacerHeight, measurementVersion, mountedRange.end, totalCount],
  );

  const updateMeasurement = useCallback((index, height) => {
    if (!Number.isFinite(height) || height <= 0) {
      return;
    }

    measurementQueueRef.current.set(index, height);

    if (measurementFrameRef.current !== null) {
      return;
    }

    measurementFrameRef.current = scheduleFrame(() => {
      measurementFrameRef.current = null;

      if (!measurementQueueRef.current.size) {
        return;
      }

      let didUpdate = false;
      measurementQueueRef.current.forEach((nextHeight, measurementIndex) => {
        const previous = measuredHeightsRef.current.get(measurementIndex);
        if (
          previous === undefined ||
          Math.abs(previous - nextHeight) > HEIGHT_EPSILON
        ) {
          measuredHeightsRef.current.set(measurementIndex, nextHeight);
          didUpdate = true;
        }
        if (nextHeight > fallbackHeightRef.current) {
          fallbackHeightRef.current = nextHeight;
        }
      });

      measurementQueueRef.current.clear();

      if (didUpdate) {
        setMeasurementVersion((version) => version + 1);
      }
    });
  }, [scheduleFrame]);

  const teardownElement = useCallback((index) => {
    const element = elementByIndexRef.current.get(index);
    if (!element) {
      return;
    }
    elementByIndexRef.current.delete(index);
    indexByElementRef.current.delete(element);
    if (observerRef.current) {
      observerRef.current.unobserve(element);
    }
  }, []);

  useEffect(() => {
    if (!totalCount) {
      return;
    }

    const { start, end } = mountedRangeRef.current;
    const overscan = measurementRetention;
    const lowerBound = Math.max(0, start - overscan);
    const upperBound = Math.min(totalCount, end + overscan);

    let removedMeasurements = false;

    measuredHeightsRef.current.forEach((_, index) => {
      if (index < lowerBound || index >= upperBound) {
        measuredHeightsRef.current.delete(index);
        removedMeasurements = true;
      }
    });

    elementByIndexRef.current.forEach((_, index) => {
      if (index < lowerBound || index >= upperBound) {
        teardownElement(index);
      }
    });

    if (removedMeasurements) {
      setMeasurementVersion((version) => version + 1);
    }
  }, [
    measurementRetention,
    mountedRange.end,
    mountedRange.start,
    teardownElement,
    totalCount,
  ]);

  const handleIntersection = useCallback(
    (entries) => {
      const container = containerRef.current;
      if (!container || !entries.length) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;
      let bestIndex = activeIndexRef.current;
      let bestDistance = Number.POSITIVE_INFINITY;
      let foundCandidate = false;

      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        const index = indexByElementRef.current.get(entry.target);
        if (typeof index !== 'number') {
          return;
        }
        const rect = entry.boundingClientRect;
        const entryCenter = rect.top + rect.height / 2;
        const distance = Math.abs(entryCenter - containerCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
          foundCandidate = true;
        }
      });

      if (foundCandidate && bestIndex !== activeIndexRef.current) {
        activeIndexRef.current = bestIndex;
        setActiveIndex(bestIndex);
      }
    },
    [containerRef],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: container,
      threshold: INTERSECTION_THRESHOLDS,
    });
    observerRef.current = observer;

    elementByIndexRef.current.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [containerRef, handleIntersection]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    if (typeof IntersectionObserver !== 'undefined') {
      return undefined;
    }

    const schedule =
      typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => setTimeout(callback, 16);
    const cancel =
      typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame.bind(window)
        : (handle) => clearTimeout(handle);

    let frameHandle = null;

    const updateFromScroll = () => {
      frameHandle = null;
      if (!totalCount) {
        return;
      }
      const targetOffset = container.scrollTop + container.clientHeight / 2;
      const fallbackHeight =
        fallbackHeightRef.current || container.clientHeight || 1;
      let cumulative = 0;
      let candidateIndex = totalCount - 1;
      for (let index = 0; index < totalCount; index += 1) {
        const measured = measuredHeightsRef.current.get(index) ?? fallbackHeight;
        cumulative += measured;
        if (targetOffset <= cumulative) {
          candidateIndex = index;
          break;
        }
      }

      if (candidateIndex !== activeIndexRef.current) {
        activeIndexRef.current = candidateIndex;
        setActiveIndex(candidateIndex);
      }
    };

    const handleScroll = () => {
      if (frameHandle !== null) {
        return;
      }
      frameHandle = schedule(updateFromScroll);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    updateFromScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (frameHandle !== null) {
        cancel(frameHandle);
      }
    };
  }, [containerRef, totalCount]);

  useEffect(
    () => () => {
      if (measurementFrameRef.current !== null) {
        cancelFrame(measurementFrameRef.current);
        measurementFrameRef.current = null;
      }
      measurementQueueRef.current.clear();
    },
    [cancelFrame],
  );

  const registerItem = useCallback(
    (index, node) => {
      if (!node) {
        teardownElement(index);
        return;
      }

      const previousIndex = indexByElementRef.current.get(node);
      if (typeof previousIndex === 'number' && previousIndex !== index) {
        elementByIndexRef.current.delete(previousIndex);
      }

      elementByIndexRef.current.set(index, node);
      indexByElementRef.current.set(node, index);

      if (observerRef.current) {
        observerRef.current.observe(node);
      }

      const rect = node.getBoundingClientRect();
      if (rect.height) {
        updateMeasurement(index, rect.height);
      }
    },
    [teardownElement, updateMeasurement],
  );

  const windowItems = useMemo(() => {
    if (!totalCount) {
      return [];
    }
    const slice = items.slice(mountedRange.start, mountedRange.end);
    const slotCount = Math.min(windowCapacity, totalCount || windowCapacity);
    return slice.map((item, offset) => {
      const index = mountedRange.start + offset;
      const slotKey = slotCount > 0 ? index % slotCount : offset;
      return {
        item,
        index,
        slotKey,
      };
    });
  }, [
    items,
    mountedRange.end,
    mountedRange.start,
    totalCount,
    windowCapacity,
  ]);

  const mountedIndices = useMemo(
    () => windowItems.map(({ index }) => index),
    [windowItems],
  );

  return {
    windowItems,
    mountedIndices,
    queue,
    meta: {
      activeIndex,
      totalCount,
    },
    beforeSpacerProps: {
      'aria-hidden': true,
      style: { height: `${beforeSpacerHeight}px` },
      'data-before-count': mountedRange.start,
      'data-mounted-start': mountedRange.start,
      'data-mounted-indices': mountedIndices.join(',') || 'none',
    },
    afterSpacerProps: {
      'aria-hidden': true,
      style: { height: `${afterSpacerHeight}px` },
      'data-after-count': Math.max(totalCount - mountedRange.end, 0),
      'data-mounted-end': Math.max(mountedRange.end - 1, -1),
      'data-mounted-indices': mountedIndices.join(',') || 'none',
    },
    registerItem,
  };
}

export default useReelWindow;
